import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tooltip"]
  static values = {
    position: { type: String, default: "top" },
    offset: { type: Number, default: 8 },
    delay: { type: Number, default: 0 }
  }
  
  connect() {
    if (!this.hasTooltipTarget) {
      console.warn("Tooltip controller requires a tooltip target");
      return;
    }
    
    this.tooltipContent = this.tooltipTarget.innerHTML;
    this.tooltipTarget.classList.add("hidden");
    
    // Properly bind event handlers
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    
    this.element.addEventListener("mouseenter", this.handleMouseEnter);
    this.element.addEventListener("mouseleave", this.hideTooltip);
    this.element.addEventListener("click", this.hideTooltip);
  }
  
  disconnect() {
    if (this.tooltipElement && document.body.contains(this.tooltipElement)) {
      document.body.removeChild(this.tooltipElement);
    }
    window.removeEventListener("scroll", this.handleScroll);
  }
  
  handleMouseEnter() {
    if (!this.setupComplete) {
      this.setupTooltip();
    }
    this.showTooltip();
  }
  
  setupTooltip() {
    this.tooltipElement = document.createElement("div");
    this.tooltipElement.innerHTML = this.tooltipContent;
    this.tooltipElement.classList.add(
      "fixed", // This positions relative to viewport
      "z-50",
      "bg-gray-800",
      "text-white",
      "px-2",
      "py-1",
      "rounded",
      "text-sm",
      "shadow-lg",
      "pointer-events-none",
      "opacity-0",
      "hidden",
      "transition-opacity",
      "duration-200"
    );
    
    document.body.appendChild(this.tooltipElement);
    this.setupComplete = true;
  }
  
  showTooltip() {
    if (!this.tooltipElement && this.setupComplete === false) {
      this.setupTooltip();
    }
    
    if (!this.tooltipElement) return;
    
    clearTimeout(this.hideTimeout);
    
    this.showTimeout = setTimeout(() => {
      this.positionTooltip();
      
      // Add scroll listener when tooltip is shown
      window.addEventListener("scroll", this.handleScroll);
      
      this.tooltipElement.classList.remove("hidden");
      
      requestAnimationFrame(() => {
        this.tooltipElement.classList.replace("opacity-0", "opacity-100");
      });
    }, this.delayValue);
  }
  
  hideTooltip() {
    if (!this.tooltipElement) return;
    
    clearTimeout(this.showTimeout);
    window.removeEventListener("scroll", this.handleScroll);
    
    this.tooltipElement.classList.replace("opacity-100", "opacity-0");
    
    this.hideTimeout = setTimeout(() => {
      this.tooltipElement.classList.add("hidden");
    }, 200);
  }
  
  // Add throttling to improve performance
  handleScroll() {
    if (this.scrollTimeout) return;
    
    this.scrollTimeout = setTimeout(() => {
      if (this.tooltipElement && !this.tooltipElement.classList.contains("hidden")) {
        this.positionTooltip();
      }
      this.scrollTimeout = null;
    }, 10); // Small timeout for better performance
  }
  
  positionTooltip() {
    if (!this.tooltipElement) return;
    
    const buttonRect = this.element.getBoundingClientRect();
    
    this.tooltipElement.classList.remove("hidden");
    this.tooltipElement.style.opacity = "0";
    
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    
    // Calculate position - for fixed positioning, we use viewport coordinates
    let top, left;
    
    switch (this.positionValue) {
      case "top":
        top = buttonRect.top - tooltipRect.height - this.offsetValue;
        left = buttonRect.left + (buttonRect.width - tooltipRect.width) / 2;
        break;
      case "right":
        top = buttonRect.top + (buttonRect.height - tooltipRect.height) / 2;
        left = buttonRect.right + this.offsetValue;
        break;
      case "bottom":
        top = buttonRect.bottom + this.offsetValue;
        left = buttonRect.left + (buttonRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = buttonRect.top + (buttonRect.height - tooltipRect.height) / 2;
        left = buttonRect.left - tooltipRect.width - this.offsetValue;
        break;
      default:
        top = buttonRect.top - tooltipRect.height - this.offsetValue;
        left = buttonRect.left + (buttonRect.width - tooltipRect.width) / 2;
    }
    
    // DO NOT add scroll position for fixed positioning
    // Instead, work directly with viewport coordinates
    
    // Constrain to viewport
    left = Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10));
    top = Math.max(10, Math.min(top, window.innerHeight - tooltipRect.height - 10));
    
    this.tooltipElement.style.top = `${top}px`;
    this.tooltipElement.style.left = `${left}px`;
    
    this.tooltipElement.style.opacity = "";
  }
}
